import React, { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, RotateCw, RefreshCcw } from "lucide-react";
import FloorPlan3DModel from "../components/floorplan/FloorPlan3DModel";

function FloorPlanPage() {
  const [transform, setTransform] = useState({
    scale: 1,
    translateX: 0,
    translateY: 0,
    rotation: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const [expanded, setExpanded] = useState("2d");

  const apartmentModelUrls = [
  "assets/models/floorplan_wall.glb",
  "assets/models/floorplan_glass.glb"
];

  // Zoom in/out floorplan image on wheel scroll
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setTransform((prev) => ({
      ...prev,
      scale: Math.max(0.5, Math.min(3, prev.scale * delta)),
    }));
  }, []);

  // Start dragging for pan
  const handleMouseDown = useCallback(
    (e) => {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - transform.translateX,
        y: e.clientY - transform.translateY,
      });
    },
    [transform.translateX, transform.translateY]
  );

  // Update pan during drag
  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      setTransform((prev) => ({
        ...prev,
        translateX: e.clientX - dragStart.x,
        translateY: e.clientY - dragStart.y,
      }));
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  // Rotate floorplan image 90 degrees
  const handleRotate = useCallback((direction) => {
    setTransform((prev) => ({
      ...prev,
      rotation: prev.rotation + direction * 90,
    }));
  }, []);

  // Reset zoom, pan, rotation
  const resetTransform = useCallback(() => {
    setTransform({
      scale: 1,
      translateX: 0,
      translateY: 0,
      rotation: 0,
    });
  }, []);

  // Touch support for pan start
  const handleTouchStart = useCallback(
    (e) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({
          x: touch.clientX - transform.translateX,
          y: touch.clientY - transform.translateY,
        });
      }
    },
    [transform.translateX, transform.translateY]
  );

  // Touch move for panning
  const handleTouchMove = useCallback(
    (e) => {
      e.preventDefault();
      if (!isDragging || e.touches.length !== 1) return;
      const touch = e.touches[0];
      setTransform((prev) => ({
        ...prev,
        translateX: touch.clientX - dragStart.x,
        translateY: touch.clientY - dragStart.y,
      }));
    },
    [isDragging, dragStart]
  );

  const handleTouchEnd = useCallback(() => setIsDragging(false), []);

  return (
    <div className="h-[100vh] -mt-20 px-4 md:px-8 lg:px-12 2xl:px-20 bg-cover bg-center bg-no-repeat">
      <div className="h-full pt-20 text-white flex flex-col pb-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 mb-6 gap-4">
          <Link
            to="/"
            className="flex items-center bg-white/20 backdrop-blur-md rounded-full px-4 py-2 text-white hover:bg-white/30 transition"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to home page
          </Link>
          <h1 className="text-4xl md:text-6xl lg:text-8xl font-bold">1 BHK</h1>
        </div>

        {/* Mobile View */}
        <div className="flex flex-col lg:hidden h-[calc(100vh-5rem)]">
          {" "}
          {/* assuming header ~80px */}
          {/* Toggle Buttons */}
          <div className="relative flex justify-center bg-white/10 rounded-xl py-3 px-6 shadow-inner backdrop-blur-sm w-full max-w-md mx-auto my-2">
            {/* Sliding indicator */}
            <div
              className={`absolute top-1 left-1 bottom-1 bg-white rounded-full shadow-md transition-transform duration-300 ease-in-out w-1/2 ${
                expanded === "3d" ? "translate-x-full" : "translate-x-0"
              }`}
            />
            {/* Buttons */}
            <button
              onClick={() => setExpanded("2d")}
              className={`relative z-10 flex-1 text-center cursor-pointer select-none font-semibold text-sm transition-colors duration-300 ${
                expanded === "2d"
                  ? "text-[#313E48]"
                  : "text-gray-400 hover:text-[#313E48]"
              }`}
            >
              2D Floor Plan
            </button>
            <button
              onClick={() => setExpanded("3d")}
              className={`relative z-10 flex-1 text-center cursor-pointer select-none font-semibold text-sm transition-colors duration-300 ${
                expanded === "3d"
                  ? "text-[#313E48]"
                  : "text-gray-400 hover:text-[#313E48]"
              }`}
            >
              3D View
            </button>
          </div>
          {/* Expandable containers */}
          <div className="flex flex-col flex-grow transition-all duration-500 ease-in-out overflow-hidden">
            {/* 2D Floor Plan */}
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                expanded === "2d" ? "flex-[6]" : "flex-[0]"
              }`}
              style={{ minHeight: 0 }}
            >
              {expanded === "2d" && (
                <div className="h-full relative py-2 flex flex-col">
                  <div
                    className="flex-1 w-full rounded-lg overflow-hidden relative cursor-grab active:cursor-grabbing"
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    style={{ touchAction: "none" }}
                  >
                    <img
                      ref={imageRef}
                      src="assets/images/floorplan.png"
                      alt="1BHK Floor Plan"
                      className="w-full h-full object-contain select-none"
                      style={{
                        transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale}) rotate(${transform.rotation}deg)`,
                        transformOrigin: "center center",
                      }}
                      draggable={false}
                    />
                    {transform.scale !== 1 && (
                      <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                        {Math.round(transform.scale * 100)}%
                      </div>
                    )}
                  </div>

                  <div className="absolute bottom-2 right-2 flex flex-col gap-2 z-10">
                    <button
                      onClick={() => handleRotate(-1)}
                      className="bg-gray-600 hover:bg-gray-500 text-white p-2 rounded-full"
                    >
                      <RotateCcw size={16} />
                    </button>
                    <button
                      onClick={() => handleRotate(1)}
                      className="bg-gray-600 hover:bg-gray-500 text-white p-2 rounded-full"
                    >
                      <RotateCw size={16} />
                    </button>
                    <button
                      onClick={resetTransform}
                      className="bg-gray-600 hover:bg-gray-500 text-white p-2 rounded-full"
                    >
                      <RefreshCcw size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 3D Model */}
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                expanded === "3d" ? "flex-[6]" : "flex-[0]"
              }`}
              style={{ minHeight: 0 }}
            >
              {expanded === "3d" && (
                <div className="h-full w-full relative rounded-lg overflow-hidden">
                  <FloorPlan3DModel modelUrls={apartmentModelUrls} />
                </div>
              )}
            </div>

            {/* 360 Virtual Tour */}
            <div
              className="flex-[4] px-4 py-4 bg-center bg-cover relative"
              style={{ backgroundImage: `url('/assets/images/360-bg.png')` }}
            >
              <div className="absolute inset-0 bg-black/50 z-0" />
              <div className="relative z-10 flex flex-col justify-center items-center h-full text-white text-center">
                <h3 className="text-2xl font-semibold mb-4">
                  360 VIRTUAL TOUR
                </h3>
                <Link
                  to="/tour"
                  className="bg-white text-[#313E48] px-6 py-2 rounded-full font-semibold"
                >
                  View Now →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop View */}
        <div className="hidden lg:flex flex-col h-full">
          <div className="w-full h-full bg-[#DCE2EB] text-[#313E48] backdrop-blur-md shadow-xl flex overflow-hidden rounded-xl">
            {/* 3D Model */}
            <div className="w-1/3 p-6 flex flex-col">
              <h3 className="text-5xl font-medium mb-4">32</h3>
              <div className="flex-1 overflow-hidden rounded-lg relative">
                <FloorPlan3DModel modelUrls={apartmentModelUrls} />
              </div>
            </div>

            {/* 2D Floor Plan */}
            <div className="w-1/3 p-6 border-x border-white/20 flex flex-col relative">
              <h2 className="text-3xl text-center mb-6">FLOOR PLAN</h2>
              <div className="flex-1 relative">
                <div
                  className="w-full h-full rounded-lg overflow-hidden relative cursor-grab active:cursor-grabbing"
                  onWheel={handleWheel}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  style={{ touchAction: "none" }}
                >
                  <img
                    ref={imageRef}
                    src="assets/images/floorplan.png"
                    alt="1BHK Floor Plan"
                    className="w-full h-full object-contain transition-transform duration-150 ease-out select-none"
                    style={{
                      transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale}) rotate(${transform.rotation}deg)`,
                      transformOrigin: "center center",
                    }}
                    draggable={false}
                  />
                  {transform.scale !== 1 && (
                    <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                      {Math.round(transform.scale * 100)}%
                    </div>
                  )}
                </div>

                <div className="absolute bottom-2 right-2 flex flex-col gap-2">
                  <button
                    onClick={() => handleRotate(-1)}
                    className="bg-gray-600 hover:bg-gray-500 text-white p-2 rounded-full"
                  >
                    <RotateCcw size={16} />
                  </button>
                  <button
                    onClick={() => handleRotate(1)}
                    className="bg-gray-600 hover:bg-gray-500 text-white p-2 rounded-full"
                  >
                    <RotateCw size={16} />
                  </button>
                  <button
                    onClick={resetTransform}
                    className="bg-gray-600 hover:bg-gray-500 text-white p-2 rounded-full"
                  >
                    <RefreshCcw size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* 360 Virtual Tour */}
            <div className="w-1/3 h-auto">
              <div
                className="h-full text-center flex flex-col justify-center relative overflow-hidden rounded-r-xl"
                style={{
                  backgroundImage: `url('/assets/images/360-bg.png')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div className="absolute inset-0 bg-black/50 z-0" />
                <div className="relative z-10 px-4">
                  <h3 className="text-4xl text-white mb-6">360 VIRTUAL TOUR</h3>
                  <Link
                    to="/tour"
                    className="bg-white transition px-8 py-3 rounded-full text-[#313E48] inline-flex items-center gap-2 text-base font-semibold"
                  >
                    View Now
                    <svg
                      className="w-5 h-5 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FloorPlanPage;
